import { useState, useRef, useEffect } from 'react';
import { Typography } from '../../base';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import PDFViewer from '../PDFViewer';
import PargodaModal from '../PargodaModal';
import {
    ViewerContainer,
    DocContainer,
    OverlayControls,
    ActionButton,
    FullscreenBody,
} from './DocumentViewer.styles';
import { FileTypes } from '../../common';
/**
 * DocumentViewer component
 * Displays PDF and DOCX files in a consistent interface
 * - Uses PDFViewer for PDF files
 * - Uses Microsoft Office Online viewer for DOCX files
 */
interface Props {
    /** URL of the document to display */
    url: string;
    /** Shows a magnifying glass that opens the document in a large modal */
    enableFullscreen?: boolean;
    fullscreenTitle?: string;
    scrollPages?: boolean;
}

const DocumentViewer = (props: Props) => {
    const {
        url,
        enableFullscreen = false,
        fullscreenTitle = '',
        scrollPages = false,
    } = props;
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const [fileType, setFileType] = useState<string>('');
    const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

    useEffect(() => {
        if (url) {
            // Extract file extension correctly, handling URLs with query parameters
            const urlWithoutParams = url.split('?')[0];
            const extension =
                urlWithoutParams.split('.').pop()?.toLowerCase() || '';
            setFileType(extension);
        }
    }, [url]);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = url;
        link.download = `document.${fileType}`;
        link.click();
    };

    const handlePrint = () => {
        const printWindow = window.open(url);
        printWindow?.print();
    };

    const fullscreenModal = enableFullscreen ? (
        <PargodaModal
            isModalOpen={isFullscreenOpen}
            setIsModalOpen={setIsFullscreenOpen}
            modalTitle={fullscreenTitle}
            sx={{
                width: '95vw',
                maxWidth: 'none',
                height: '95vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <FullscreenBody>
                <DocumentViewer url={url} scrollPages />
            </FullscreenBody>
        </PargodaModal>
    ) : null;

    // If the file is a PDF, use the PDFViewer component
    if (fileType === FileTypes.pdf) {
        return (
            <>
                <PDFViewer
                    url={url}
                    scrollPages={scrollPages}
                    showFullscreenButton={enableFullscreen}
                    onFullscreen={() => setIsFullscreenOpen(true)}
                />
                {fullscreenModal}
            </>
        );
    }

    // For DOC and DOCX files, display in an iframe
    return (
        <ViewerContainer ref={containerRef}>
            {fileType === FileTypes.docx ? (
                <DocContainer>
                    <iframe
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        title="Document Viewer"
                    />
                </DocContainer>
            ) : (
                <Typography color="error">
                    {t(
                        'components.unsupportedFileType',
                        'Unsupported file type'
                    )}
                </Typography>
            )}

            {fileType === FileTypes.docx && (
                <OverlayControls>
                    {enableFullscreen && (
                        <ActionButton
                            onClick={() => setIsFullscreenOpen(true)}
                            aria-label={t('components.viewFullscreen')}
                        >
                            <SearchIcon />
                        </ActionButton>
                    )}
                    <ActionButton
                        onClick={handleDownload}
                        aria-label={t('components.download')}
                    >
                        <DownloadIcon />
                    </ActionButton>
                    <ActionButton
                        onClick={handlePrint}
                        aria-label={t('components.print')}
                    >
                        <PrintIcon />
                    </ActionButton>
                </OverlayControls>
            )}
            {fullscreenModal}
        </ViewerContainer>
    );
};

export default DocumentViewer;
